import React from "react";
import { Star } from "lucide-react";
import { useTranslation } from "react-i18next";

// Preuve sociale sur la home : on remonte les avis Google réels (déjà affichés
// sur /tarifs) + la note 5,0/5. Les avis défilent en marquee horizontal infini.
const GOOGLE_LOGO =
  "https://media.base44.com/images/public/6a69bb99139c43be5222264c/feecd347d_google-logo.jpg";

export default function HomeReviews({ showBadge = true }) {
  const { t } = useTranslation();
  const reviews = t("tarifs.reviews", { returnObjects: true }) || [];
  const list = Array.isArray(reviews) ? reviews : [];

  // On répète la liste assez de fois pour qu'une moitié de la piste soit plus
  // large que n'importe quel viewport (sinon translateX(-50%) laisse un trou à
  // droite sur les écrans larges). 8 copies = 2 moitiés identiques de 4 jeux.
  const loop = [...list, ...list, ...list, ...list, ...list, ...list, ...list, ...list];

  return (
    <section className="relative py-20 px-6 overflow-hidden">
      <div className="max-w-4xl mx-auto">
        <div className="text-center mb-12">
          {showBadge && (
          <span className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full tx-glass-strong text-xs text-[#0985D8] mb-5">
            {t("home.reviews.badge")}
          </span>
          )}
          <div className="flex items-center justify-center gap-2 mb-3">
            <img src={GOOGLE_LOGO} alt="Google" className="w-7 h-7 rounded-full object-contain" />
            <span className="font-bold text-[#001D31] text-xl">{t("home.reviews.title1")}</span>
            <div className="flex gap-0.5">
              {[...Array(5)].map((_, i) => (
                <Star key={i} className="w-4 h-4 fill-[#FCE300] text-[#FCE300]" />
              ))}
            </div>
            <span className="text-[#8896B0] text-sm">{t("tarifs.reviewsGoogle")}</span>
          </div>
          <h2 className="text-2xl md:text-3xl font-semibold [font-family:'Playfair_Display',_serif] mb-3">
            {t("home.reviews.title2")}
          </h2>
          <p className="max-w-xl mx-auto text-[#8896B0] text-base">{t("home.reviews.subtitle")}</p>
        </div>
      </div>

      {/* Marquee horizontal infini — pause au survol */}
      <div className="reviews-marquee">
        <div className="reviews-track">
          {loop.map((r, i) => (
            <div key={i} className="reviews-card tx-glass rounded-2xl p-6 flex flex-col gap-3">
              <div className="flex gap-0.5">
                {[...Array(5)].map((_, j) => (
                  <Star key={j} className="w-3.5 h-3.5 fill-[#FCE300] text-[#FCE300]" />
                ))}
              </div>
              <p className="text-sm text-[#001D31]/80 leading-relaxed flex-1">"{r.text}"</p>
              <p className="text-xs font-semibold text-[#0985D8]">{r.name}</p>
            </div>
          ))}
        </div>
      </div>

      <style>{`
        .reviews-marquee {
          position: relative;
          width: 100%;
          overflow: hidden;
          -webkit-mask-image: linear-gradient(90deg, transparent 0, #000 8%, #000 92%, transparent 100%);
          mask-image: linear-gradient(90deg, transparent 0, #000 8%, #000 92%, transparent 100%);
        }
        .reviews-track {
          display: flex;
          width: max-content;
          animation: reviews-scroll 40s linear infinite;
        }
        .reviews-marquee:hover .reviews-track {
          animation-play-state: paused;
        }
        /* marge a droite plutot que gap : la largeur de chaque carte
           (carte + marge) est identique, donc translateX(-50%) retombe
           exactement sur le debut de la 2eme copie : boucle seamless. */
        .reviews-card {
          width: 340px;
          margin-right: 20px;
          flex: 0 0 auto;
        }
        @keyframes reviews-scroll {
          from { transform: translateX(0); }
          to { transform: translateX(-50%); }
        }
        @media (prefers-reduced-motion: reduce) {
          .reviews-track { animation: none; }
        }
        /* Mobile : animations désactivées par le CSS global → on rend la
           piste scrollable au toucher pour ne pas masquer les avis. */
        @media (max-width: 768px) {
          .reviews-marquee {
            -webkit-mask-image: none;
            mask-image: none;
            overflow-x: auto;
            scroll-snap-type: x mandatory;
            padding-bottom: 8px;
          }
          .reviews-track {
            animation: none;
          }
          .reviews-card {
            width: 78vw;
            margin-right: 14px;
            scroll-snap-align: center;
          }
        }
      `}</style>
    </section>
  );
}